import React, { ReactNode } from 'react';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Document, Block, Inline } from '@contentful/rich-text-types';

interface ContentfulRichTextProps {
  content?: Document | null;
  className?: string;
}

// Custom rendering options to preserve styling
const richTextOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children: ReactNode) => (
      <p className="rich-text-paragraph">{children}</p>
    ),
    [BLOCKS.HEADING_1]: (_node: Block | Inline, children: ReactNode) => (
      <h1 className="rich-text-h1">{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (_node: Block | Inline, children: ReactNode) => (
      <h2 className="rich-text-h2">{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (_node: Block | Inline, children: ReactNode) => (
      <h3 className="rich-text-h3">{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (_node: Block | Inline, children: ReactNode) => (
      <h4 className="rich-text-h4">{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (_node: Block | Inline, children: ReactNode) => (
      <h5 className="rich-text-h5">{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (_node: Block | Inline, children: ReactNode) => (
      <h6 className="rich-text-h6">{children}</h6>
    ),
    [BLOCKS.UL_LIST]: (_node: Block | Inline, children: ReactNode) => (
      <ul className="rich-text-ul">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node: Block | Inline, children: ReactNode) => (
      <ol className="rich-text-ol">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_node: Block | Inline, children: ReactNode) => (
      <li className="rich-text-li">{children}</li>
    ),
    [BLOCKS.QUOTE]: (_node: Block | Inline, children: ReactNode) => (
      <blockquote className="rich-text-quote">{children}</blockquote>
    ),
    [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
      const { uri } = node.data;
      return (
        <a 
          href={uri} 
          className="rich-text-link"
          target={uri.startsWith('http') ? '_blank' : '_self'}
          rel={uri.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      );
    },
  },
};

/**
 * ContentfulRichText component for rendering rich text from Contentful
 * Preserves formatting and applies consistent styling
 */
export const ContentfulRichText: React.FC<ContentfulRichTextProps> = ({ 
  content, 
  className 
}) => {
  if (!content) return null;
  
  return (
    <div className={`contentful-rich-text ${className || ''}`}>
      {documentToReactComponents(content, richTextOptions)}
    </div>
  );
};

// Export a simple HTML renderer for cases where content might be HTML string
export const ContentfulHTML: React.FC<{ content?: string | null; className?: string }> = ({ 
  content, 
  className 
}) => {
  if (!content) return null;
  
  return (
    <div 
      className={`contentful-html ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};